import { Link } from '@tanstack/react-router';
import { MailCheck } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { AuthPage } from '../components/auth-page';
import { ServerError } from '../components/server-error';
import { useForgotPassword } from '../hooks/use-forgot-password';
import { forgotPasswordSchema, type ForgotPasswordValues } from '../schemas/password-schemas';

/** Requests a reset link. The backend answers 202 whether or not the address has an account. */
export function ForgotPasswordPage() {
  const { t } = useTranslation('auth');
  const forgot = useForgotPassword();
  const form = useForm<ForgotPasswordValues>({
    resolver: useI18nResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);
  const backToSignIn = <Link to="/login">{t('links.backToSignIn')}</Link>;

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await forgot.mutateAsync(values);
    } catch (error) {
      applyFieldErrors(error);
    }
  });

  if (forgot.isSuccess) {
    return (
      <AuthPage title={t('forgot.sentTitle')} footer={backToSignIn}>
        <Alert role="status">
          <MailCheck aria-hidden />
          <AlertTitle>{t('forgot.sent')}</AlertTitle>
          <AlertDescription>{t('forgot.sentHint')}</AlertDescription>
        </Alert>
      </AuthPage>
    );
  }

  const rateLimit =
    isApiError(forgot.error) && forgot.error.kind === 'rate_limited' ? forgot.error.meta : null;
  const retryAfter =
    typeof rateLimit?.retryAfterSeconds === 'number' ? rateLimit.retryAfterSeconds : null;

  return (
    <AuthPage title={t('forgot.title')} description={t('forgot.description')} footer={backToSignIn}>
      <Form {...form}>
        <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
          {rateLimit ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>
                {retryAfter === null
                  ? t('forgot.rateLimitedGeneric')
                  : t('forgot.rateLimited', { count: retryAfter })}
              </AlertDescription>
            </Alert>
          ) : forgot.isError && !forgot.error.hasFieldErrors ? (
            <ServerError error={forgot.error} />
          ) : null}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('fields.email')}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="email"
                    autoFocus
                    placeholder={t('fields.emailPlaceholder')}
                    className="h-11"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" size="lg" className="h-11 w-full" disabled={forgot.isPending}>
            {forgot.isPending ? t('forgot.submitting') : t('forgot.submit')}
          </Button>
        </form>
      </Form>
    </AuthPage>
  );
}
